import type { Position } from "../types.js";
import { isWalkable, type TilemapJson } from "./tilemap.js";

const SQRT2 = Math.SQRT2;
const MAX_EXPANSIONS = 4096;

interface Neighbor {
  dx: number;
  dy: number;
  cost: number;
  diagonal: boolean;
}

const NEIGHBORS: Neighbor[] = [
  { dx: 1, dy: 0, cost: 1, diagonal: false },
  { dx: -1, dy: 0, cost: 1, diagonal: false },
  { dx: 0, dy: 1, cost: 1, diagonal: false },
  { dx: 0, dy: -1, cost: 1, diagonal: false },
  { dx: 1, dy: 1, cost: SQRT2, diagonal: true },
  { dx: 1, dy: -1, cost: SQRT2, diagonal: true },
  { dx: -1, dy: 1, cost: SQRT2, diagonal: true },
  { dx: -1, dy: -1, cost: SQRT2, diagonal: true },
];

function octile(ax: number, ay: number, bx: number, by: number): number {
  const dx = Math.abs(ax - bx);
  const dy = Math.abs(ay - by);
  return Math.max(dx, dy) + (SQRT2 - 1) * Math.min(dx, dy);
}

class MinHeap {
  private readonly items: { key: number; value: number }[] = [];

  push(key: number, value: number): void {
    this.items.push({ key, value });
    this.siftUp(this.items.length - 1);
  }

  pop(): number | undefined {
    if (this.items.length === 0) return undefined;
    const top = this.items[0]!.value;
    const last = this.items.pop()!;
    if (this.items.length > 0) {
      this.items[0] = last;
      this.siftDown(0);
    }
    return top;
  }

  get size(): number {
    return this.items.length;
  }

  private siftUp(i: number): void {
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.items[parent]!.key <= this.items[i]!.key) break;
      [this.items[parent], this.items[i]] = [this.items[i]!, this.items[parent]!];
      i = parent;
    }
  }

  private siftDown(i: number): void {
    const n = this.items.length;
    while (true) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let smallest = i;
      if (l < n && this.items[l]!.key < this.items[smallest]!.key) smallest = l;
      if (r < n && this.items[r]!.key < this.items[smallest]!.key) smallest = r;
      if (smallest === i) break;
      [this.items[i], this.items[smallest]] = [this.items[smallest]!, this.items[i]!];
      i = smallest;
    }
  }
}

// M11 — early-exit guard. A mob never legitimately pathfinds farther than
// its aggro range (currently up to ~12 tiles). 64 tiles is generous
// enough for any non-pathological case and short-circuits a cross-zone
// chase that would burn MAX_EXPANSIONS on a 1024² grid.
const MAX_PATH_RADIUS = 64;

export function findPath(map: TilemapJson, start: Position, goal: Position): Position[] | null {
  if (!isWalkable(map, goal.x, goal.y)) return null;
  if (start.x === goal.x && start.y === goal.y) return [];
  if (
    Math.abs(start.x - goal.x) > MAX_PATH_RADIUS ||
    Math.abs(start.y - goal.y) > MAX_PATH_RADIUS
  ) {
    return null;
  }

  const w = map.width;
  const startId = start.y * w + start.x;
  const goalId = goal.y * w + goal.x;

  // M11 — sparse storage. The legacy Float64Array(width*height) allocated
  // 8 MB per call on a 1024² zone (and another 4 MB for cameFrom). With
  // 50 mobs pathfinding ~1×/s that's 600 MB/s of GC churn. Maps trade a
  // small per-lookup cost for zero up-front allocation; the lazy-fill
  // touches at most MAX_EXPANSIONS entries.
  const gScore = new Map<number, number>();
  const cameFrom = new Map<number, number>();
  const closed = new Set<number>();
  gScore.set(startId, 0);

  const open = new MinHeap();
  open.push(octile(start.x, start.y, goal.x, goal.y), startId);

  let expansions = 0;

  while (open.size > 0 && expansions < MAX_EXPANSIONS) {
    const current = open.pop()!;
    if (current === goalId) return reconstruct(cameFrom, current, w);
    if (closed.has(current)) continue;
    closed.add(current);
    expansions++;

    const cx = current % w;
    const cy = Math.floor(current / w);
    const currentG = gScore.get(current) ?? Infinity;

    for (const n of NEIGHBORS) {
      const nx = cx + n.dx;
      const ny = cy + n.dy;
      if (!isWalkable(map, nx, ny)) continue;
      if (n.diagonal) {
        if (!isWalkable(map, cx + n.dx, cy) || !isWalkable(map, cx, cy + n.dy)) continue;
      }
      const neighborId = ny * w + nx;
      if (closed.has(neighborId)) continue;
      const tentative = currentG + n.cost;
      const existing = gScore.get(neighborId) ?? Infinity;
      if (tentative >= existing) continue;
      gScore.set(neighborId, tentative);
      cameFrom.set(neighborId, current);
      const f = tentative + octile(nx, ny, goal.x, goal.y);
      open.push(f, neighborId);
    }
  }

  return null;
}

function reconstruct(cameFrom: Map<number, number>, goalId: number, w: number): Position[] {
  const path: Position[] = [];
  let cur: number | undefined = goalId;
  while (cur !== undefined) {
    path.push({ x: cur % w, y: Math.floor(cur / w) });
    cur = cameFrom.get(cur);
  }
  path.reverse();
  path.shift();
  return path;
}
